import { Alert, Button, Form, Input, Modal, Select, Typography } from "antd";
import {
  EyeInvisibleOutlined,
  EyeTwoTone,
  LockOutlined,
  UserOutlined,
} from "@ant-design/icons";
import "../../../styles/trackon.css"

const { Title, Text } = Typography;



export function ForgotPasswordModal({
  open,
  loading,
  success,
  mode,
  step,
  error,
  securityQuestions,
  form,
  onCancel,
  onGetQuestion,
  onSubmit,
}) {
  const showResetFields = step === "reset" || mode === "adminReset";

  return (
    <Modal
    className="trackon-root"
      title={
        <div className="login-modal__title">
          <LockOutlined style={{ color: "#1677ff" }} />
          <span className="login-modal__title-text">Reset Password</span>
        </div>
      }
      open={open}
      onCancel={onCancel}
      footer={
        success ? (
          <Button
            type="primary"
            block
            onClick={onCancel}
            className="login-modal__button login-modal__button--primary"
          >
            Back to Sign In
          </Button>
        ) : (
          <div className="login-modal__footer">
            <Button block onClick={onCancel} className="login-modal__button">
              Cancel
            </Button>
            <Button
              type="primary"
              block
              loading={loading}
              onClick={async () => {
                try {
                  if (mode === "forgot" && step === "uid") {
                    await onGetQuestion();
                    return;
                  }

                  const values = await form.validateFields();
                  await onSubmit(values);
                } catch {
                  // validation errors are handled inline by Form
                }
              }}
              className="login-modal__button login-modal__button--primary"
            >
              {mode === "forgot" && step === "uid" ? "Submit" : "Reset Password"}
            </Button>
          </div>
        )
      }
      width={440}
      centered
      destroyOnClose
    >
      {success ? (
        <div className="login-forgot-success">
          <div className="login-forgot-success__icon">✅</div>
          <Title level={5} className="login-forgot-success__title">
            Password Reset Successful!
          </Title>
          <Text className="login-forgot-success__text">
            Your password has been updated. You can now sign in with your new password.
          </Text>
        </div>
      ) : (
        <>
          <Text className="login-forgot-copy">
            {mode === "adminReset"
              ? "Choose a security question, answer it, and set a new password."
              : step === "uid"
                ? "Enter your UST UID to begin resetting your password"
                : "Answer your security question and set a new password."}
          </Text>

          {error && (
            <Alert
              type="error"
              showIcon
              message={error}
              style={{ marginBottom: 12, borderRadius: 8 }}
            />
          )}

          <Form
            form={form}
            layout="vertical"
            requiredMark={false}
            onFinish={onSubmit}
          >
            <Form.Item
              label={<Text className="login-forgot-label">UST UID</Text>}
              name="uid"
              rules={[
                { required: true, message: "UST UID is required" },
                { len: 6, message: "UST UID must be exactly 6 characters." },
              ]}
              className="login-form-item"
            >
              <Input
                prefix={<UserOutlined className="login-input__icon" />}
                placeholder="Enter your UST UID"
                size="middle"
                className="login-input"
                disabled={mode === "adminReset" || step === "reset"}
              />
            </Form.Item>

            {showResetFields && (
              <>
                <Form.Item
                  label={<Text className="login-forgot-label">Security Question</Text>}
                  name="securityQuestion"
                  rules={[{ required: true, message: "Please select a security question" }]}
                  className="login-form-item"
                >
                  {mode === "adminReset" ? (
                    <Select
                      placeholder="Select security question"
                      size="middle"
                      className="login-input"
                      options={securityQuestions.map((item) => ({
                        value: String(item.quesId),
                        label: item.question,
                      }))}
                      onChange={(value, option) => {
                        const selected = option;
                        form.setFieldsValue({
                          quesId: Number(value),
                          securityQuestion: String(selected.label ?? ""),
                        });
                      }}
                    />
                  ) : (
                    <Input
                      size="middle"
                      className="login-input"
                      disabled
                    />
                  )}
                </Form.Item>

                <Form.Item name="quesId" hidden>
                  <Input type="hidden" />
                </Form.Item>

                <Form.Item
                  label={<Text className="login-forgot-label">Security Answer</Text>}
                  name="securityAnswer"
                  rules={[
                    { required: true, message: "Security answer is required" },
                    { min: 2, message: "Please enter a valid answer" },
                  ]}
                  className="login-form-item"
                >
                  <Input.Password
                    placeholder="Enter your security answer"
                    size="middle"
                    className="login-input"
                  />
                </Form.Item>

                <Form.Item
                  label={<Text className="login-forgot-label">New Password</Text>}
                  name="newPassword"
                  rules={[
                    { required: true, message: "New password is required" },
                    { min: 8, message: "Password must be at least 8 characters" },
                    {
                      pattern: /^(?=.*[A-Z])(?=.*\d)/,
                      message: "Must contain an uppercase letter and a number",
                    },
                  ]}
                  className="login-form-item"
                >
                  <Input.Password
                    prefix={<LockOutlined className="login-input__icon" />}
                    placeholder="Enter new password"
                    size="middle"
                    className="login-input"
                    autoComplete="new-password"
                    iconRender={(visible) =>
                      visible ? <EyeTwoTone /> : <EyeInvisibleOutlined />
                    }
                  />
                </Form.Item>

                <Form.Item
                  label={<Text className="login-forgot-label">Confirm Password</Text>}
                  name="confirmPassword"
                  dependencies={["newPassword"]}
                  rules={[
                    { required: true, message: "Please confirm your password" },
                    ({ getFieldValue }) => ({
                      validator(_, value) {
                        if (!value || getFieldValue("newPassword") === value) {
                          return Promise.resolve();
                        }
                        return Promise.reject(new Error("Passwords do not match"));
                      },
                    }),
                  ]}
                  className="login-form-item"
                >
                  <Input.Password
                    prefix={<LockOutlined className="login-input__icon" />}
                    placeholder="Re-enter new password"
                    size="middle"
                    className="login-input"
                    autoComplete="new-password"
                    iconRender={(visible) =>
                      visible ? <EyeTwoTone /> : <EyeInvisibleOutlined />
                    }
                  />
                </Form.Item>
              </>
            )}
          </Form>
        </>
      )}
    </Modal>
  );
}